import React, { useEffect, useState, useRef } from "react";
import Calling from '../../api/Calling';
import Loader from '../../components/Loader';
import { toast } from "react-toastify";
import * as echarts from 'echarts';
import {
    FiCalendar,
    FiTrendingUp,
    FiActivity,
    FiPhone,
    FiPhoneMissed,
    FiPhoneOff,
    FiBox,
    FiSearch,
    FiFilter,
    FiAlertCircle,
    FiXCircle,
    FiDownload,
    FiRefreshCw,
    FiEye,
    FiPhoneCall,
    FiChevronRight
} from "react-icons/fi";
import MainButton from "../../components/ul/MainButton";
import getISTDate from "../../components/getISTDate";
import formatDate from "../../components/formatDate";

const ShowBoxTrack = () => {
    const [data, setData] = useState([]);
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());
    const [loading, setLoading] = useState(false);
    const [selectedBtn, setSelectedBtn] = useState('today');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [selectedEventFilter, setSelectedEventFilter] = useState('all');

    // ECharts ref
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    // Event types configuration - dailing_box moved to first position
    const eventTypes = ['dailing_box', 'ring_box', 'miss_box', 'end_box', 'search_box'];

    const eventConfig = {
        dailing_box: {
            label: 'Dailing Box',
            icon: FiPhoneCall,
            color: '#34C759',
            bgColor: 'bg-emerald-50',
            textColor: 'text-main-blue',
            gradient: 'from-main-blue to-emerald-600',
            borderColor: 'border-main-blue',
            lightBg: '#f2fcf5'
        },
        ring_box: {
            label: 'Ring Box',
            icon: FiPhone,
            color: '#007bff',
            bgColor: 'bg-blue-50',
            textColor: 'text-main-exBlue',
            gradient: 'from-main-exBlue to-blue-600',
            borderColor: 'border-main-exBlue',
            lightBg: '#f0f7ff'
        },
        miss_box: {
            label: 'Miss Box',
            icon: FiPhoneMissed,
            color: '#F34235',
            bgColor: 'bg-red-50',
            textColor: 'text-main-red',
            gradient: 'from-main-red to-red-600',
            borderColor: 'border-main-red',
            lightBg: '#fff5f4'
        },
        end_box: {
            label: 'End Box',
            icon: FiPhoneOff,
            color: '#10b981',
            bgColor: 'bg-teal-50',
            textColor: 'text-teal-600',
            gradient: 'from-teal-500 to-teal-600',
            borderColor: 'border-teal-400',
            lightBg: '#f0fdfa'
        },
        search_box: {
            label: 'Search Box',
            icon: FiSearch,
            color: '#FFC107',
            bgColor: 'bg-amber-50',
            textColor: 'text-main-yellow',
            gradient: 'from-main-yellow to-amber-500',
            borderColor: 'border-main-yellow',
            lightBg: '#fffbeb'
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await Calling.getShowboxAnalytic(startDate, endDate);
            console.log('API Response:', response);
            if (response?.status) {
                setData(response?.data || []);
            } else {
                toast.error(response?.message || "Failed to fetch ShowBox data");
            }
        } catch (error) {
            console.error("Error fetching ShowBox data:", error);
            toast.error("Server error while fetching ShowBox data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, selectedBtn]);

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000;
            const istDate = new Date(date.getTime() + istOffset);
            return istDate.toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectedBtn('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectedBtn('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);
                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectedBtn('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectedBtn('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectedBtn('last6Months');
                break;

            case 'thisYear':
                const firstDayOfYearUTC = new Date(todayUTC.getFullYear(), 0, 1);
                const lastDayOfYearUTC = new Date(todayUTC.getFullYear(), 11, 31);
                start = toIST(firstDayOfYearUTC);
                end = toIST(lastDayOfYearUTC);
                setSelectedBtn('thisYear');
                break;

            case 'lastYear':
                const firstDayLastYearUTC = new Date(todayUTC.getFullYear() - 1, 0, 1);
                const lastDayLastYearUTC = new Date(todayUTC.getFullYear() - 1, 11, 31);
                start = toIST(firstDayLastYearUTC);
                end = toIST(lastDayLastYearUTC);
                setSelectedBtn('lastYear');
                break;

            default:
                start = '';
                end = '';
                setSelectedBtn('range');
        }

        setStartDate(start);
        setEndDate(end);
    };

    const handleFormDate = (e) => {
        const { name, value } = e.target;
        if (name === 'startDate') {
            setStartDate(value);
        } else {
            setEndDate(value);
        }
        setSelectedBtn('range');
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchData();
    };

    const handleDeleteData = async () => {
        if (!startDate || !endDate) {
            toast.error("Please select a valid date range");
            return;
        }

        setDeleteLoading(true);
        try {
            const response = await Calling.deleteShowboxAnalytic(startDate, endDate);

            if (response?.status) {
                toast.success(response?.message || "Data deleted successfully");
                fetchData();
                setShowDeleteModal(false);
            } else {
                toast.error(response?.message || "Failed to delete data");
            }
        } catch (error) {
            console.error("Error deleting data:", error);
            toast.error("Server error while deleting data");
        } finally {
            setDeleteLoading(false);
        }
    };

    // Get filtered data based on selected event filter
    const getFilteredData = () => {
        if (selectedEventFilter === 'all') return data;
        return data.filter(item => item.a_event === selectedEventFilter);
    };

    const filteredData = getFilteredData();

    // Calculate analytics
    const getAnalytics = () => {
        const analytics = {
            total: 0,
            dailing_box: 0,
            ring_box: 0,
            miss_box: 0,
            end_box: 0,
            search_box: 0
        };

        data.forEach(item => {
            const count = item.a_count || 0;
            analytics.total += count;
            if (analytics[item.a_event] !== undefined) {
                analytics[item.a_event] += count;
            }
        });

        return analytics;
    };

    const analytics = getAnalytics();

    // Group data by date for chart
    const getChartData = () => {
        const eventsByDate = {};

        filteredData.forEach((item) => {
            const date = formatDate(item.createdAt);

            if (!eventsByDate[date]) {
                eventsByDate[date] = {};
            }
            if (!eventsByDate[date][item.a_event]) {
                eventsByDate[date][item.a_event] = 0;
            }
            eventsByDate[date][item.a_event] += item.a_count || 0;
        });

        const labels = Object.keys(eventsByDate).sort((a, b) => new Date(a) - new Date(b));
        const activeEventTypes = selectedEventFilter === 'all'
            ? eventTypes
            : [selectedEventFilter];

        return { eventsByDate, labels, activeEventTypes };
    };

    const { eventsByDate, labels: chartLabels, activeEventTypes } = getChartData();

    // ECharts initialization and update
    useEffect(() => {
        if (!chartRef.current || filteredData.length === 0) return;

        // Initialize chart if not exists
        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }

        const chart = chartInstance.current;

        const series = activeEventTypes.map((eventType) => {
            const config = eventConfig[eventType];
            const color = config?.color || '#9ca3af';

            return {
                name: config?.label || eventType,
                type: 'line',
                smooth: true,
                symbol: 'circle',
                symbolSize: 8,
                lineStyle: {
                    width: 3,
                    color: color
                },
                itemStyle: {
                    color: color,
                    borderColor: '#fff',
                    borderWidth: 2
                },
                areaStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: color + '30' },
                        { offset: 1, color: color + '05' }
                    ])
                },
                emphasis: {
                    focus: 'series',
                    itemStyle: {
                        borderWidth: 3,
                        shadowBlur: 10,
                        shadowColor: color + '60'
                    }
                },
                data: chartLabels.map(date => eventsByDate[date]?.[eventType] || 0)
            };
        });

        const option = {
            tooltip: {
                trigger: 'axis',
                backgroundColor: '#111723',
                borderColor: 'transparent',
                borderRadius: 12,
                padding: [12, 16],
                textStyle: {
                    color: '#fff',
                    fontSize: 13
                },
                axisPointer: {
                    type: 'cross',
                    crossStyle: {
                        color: '#CFD1D3'
                    }
                },
                formatter: function (params) {
                    let result = `<div style="font-weight: 600; margin-bottom: 10px; font-size: 14px;">${params[0].axisValue}</div>`;
                    params.forEach(param => {
                        result += `<div style="display: flex; align-items: center; justify-content: space-between; margin: 6px 0;">
                            <span style="display: flex; align-items: center;">
                                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${param.color}; margin-right: 10px;"></span>
                                <span style="color: #888B91;">${param.seriesName}</span>
                            </span>
                            <span style="font-weight: 700; margin-left: 24px; color: #fff;">${param.value.toLocaleString()}</span>
                        </div>`;
                    });
                    return result;
                }
            },
            legend: {
                type: 'scroll',
                top: 0,
                left: 'center',
                icon: 'circle',
                itemWidth: 8,
                itemHeight: 8,
                itemGap: 24,
                textStyle: {
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#111723'
                }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                top: '15%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: chartLabels,
                axisLine: {
                    lineStyle: {
                        color: '#CFD1D3'
                    }
                },
                axisLabel: {
                    color: '#888B91',
                    fontSize: 11,
                    rotate: chartLabels.length > 10 ? 45 : 0
                },
                axisTick: {
                    show: false
                }
            },
            yAxis: {
                type: 'value',
                minInterval: 1,
                axisLine: {
                    show: false
                },
                axisLabel: {
                    color: '#888B91',
                    fontSize: 11
                },
                splitLine: {
                    lineStyle: {
                        color: '#CFD1D3',
                        type: 'dashed',
                        opacity: 0.5
                    }
                }
            },
            series: series
        };

        chart.setOption(option, true);

        const handleResize = () => {
            chart?.resize();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [filteredData, chartLabels, activeEventTypes, selectedEventFilter, eventsByDate]);

    // Cleanup chart on unmount
    useEffect(() => {
        const chart = chartInstance.current;
        return () => {
            if (chart) {
                chart.dispose();
            }
            chartInstance.current = null;
        };
    }, []);

    // Export to CSV
    const handleExportCSV = () => {
        try {
            if (filteredData.length === 0) {
                toast.warning("No data available to export");
                return;
            }

            const exportData = filteredData.map((item, index) => ({
                '#': index + 1,
                'Event Type': eventConfig[item.a_event]?.label || item.a_event,
                'Count': item.a_count,
                'Created At': formatDate(item.createdAt),
                'Updated At': formatDate(item.updatedAt),
            }));

            const headers = Object.keys(exportData[0]).join(',');
            const rows = exportData.map(row =>
                Object.values(row).map(value =>
                    typeof value === 'string' && (value.includes(',') || value.includes('"'))
                        ? `"${value.replace(/"/g, '""')}"`
                        : value
                ).join(',')
            );

            const csv = [headers, ...rows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.setAttribute('href', url);
            link.setAttribute('download', `ShowBoxAnalytics_${startDate}_to_${endDate}.csv`);
            link.style.visibility = 'hidden';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success("CSV file downloaded successfully");
        } catch (error) {
            console.error("Error exporting CSV:", error);
            toast.error("Failed to export CSV");
        }
    };

    // Delete Modal Component
    const DeleteModal = () => {
        return (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-main-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
                    {/* Modal Header */}
                    <div className="bg-main-red/10 p-5">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-main-red/20 rounded-2xl flex items-center justify-center">
                                <FiAlertCircle className="text-main-red text-3xl" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-main-font">Delete ShowBox Data</h3>
                                <p className="text-sm text-main-subfont mt-0.5">This action cannot be undone</p>
                            </div>
                        </div>
                    </div>

                    <div className="p-5">
                        <div className="space-y-4">
                            <p className="text-main-subfont text-sm">
                                You are about to permanently delete ShowBox analytics data for the selected period.
                            </p>

                            <div className="bg-main-bg rounded-xl p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-main-subfont">Date Range</span>
                                    <span className="text-sm font-semibold text-main-font">
                                        {startDate} → {endDate}
                                    </span>
                                </div>
                                <div className="h-px bg-main-border"></div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-main-subfont">Records to Delete</span>
                                    <span className="text-sm font-bold text-main-red">
                                        {data.length} records
                                    </span>
                                </div>
                            </div>

                            <div className="bg-main-yellow/10 border border-main-yellow/30 rounded-xl p-4">
                                <p className="text-sm text-main-font font-medium flex items-start gap-2">
                                    <span className="text-main-yellow text-lg">⚠</span>
                                    <span>All data matching this date range will be permanently removed from the system.</span>
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                disabled={deleteLoading}
                                className="flex-1 px-5 py-3 bg-main-bg text-main-font rounded-xl font-semibold hover:bg-gray-100 transition-all disabled:opacity-50 border border-main-border"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteData}
                                disabled={deleteLoading}
                                className="flex-1 px-5 py-3 bg-main-red text-main-white rounded-xl font-semibold hover:bg-red-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-main-red/25"
                            >
                                {deleteLoading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>Deleting...</span>
                                    </>
                                ) : (
                                    <>
                                        <FiXCircle className="text-lg" />
                                        <span>Delete Data</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className='w-full min-h-screen text-main-font'>
            {/* Page Header */}
            <div className="mb-4">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-main-blue to-emerald-600 flex items-center justify-center text-main-white shadow-lg shadow-main-blue/30">
                        <FiBox size={26} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font tracking-tight">ShowBox Analytics</h1>
                        <p className="text-sm text-main-subfont mt-1">Track and analyze your ShowBox events in real-time</p>
                    </div>
                </div>
            </div>
            <div className="bg-main-bg p-4 rounded-xl">

                {/* Date Range Selection Card */}
                <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden mb-6">
                    <div className="p-5">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-10 h-10 bg-main-blue/10 rounded-xl flex items-center justify-center">
                                <FiCalendar className="text-main-blue text-xl" />
                            </div>
                            <div>
                                <h2 className="text-base font-semibold text-main-font">Date Range</h2>
                                <p className="text-xs text-main-subfont">Select a time period to analyze</p>
                            </div>
                        </div>

                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
                            {/* Date Range Buttons */}
                            <div className="flex flex-wrap gap-2">
                                <MainButton
                                    onClick={() => handleDateRangeFilter('today')}
                                    label={'Today'}
                                    selectState={selectedBtn === "today"}
                                />
                                <MainButton
                                    onClick={() => handleDateRangeFilter('yesterday')}
                                    label={'Yesterday'}
                                    selectState={selectedBtn === "yesterday"}
                                />
                                <MainButton
                                    onClick={() => handleDateRangeFilter('thisWeek')}
                                    label={'This Week'}
                                    selectState={selectedBtn === "thisWeek"}
                                />
                                <MainButton
                                    onClick={() => handleDateRangeFilter('thisMonth')}
                                    label={'This Month'}
                                    selectState={selectedBtn === "thisMonth"}
                                />
                                <MainButton
                                    onClick={() => handleDateRangeFilter('last6Months')}
                                    label={'Last 6 Months'}
                                    selectState={selectedBtn === "last6Months"}
                                />
                                <MainButton
                                    onClick={() => handleDateRangeFilter('thisYear')}
                                    label={'This Year'}
                                    selectState={selectedBtn === "thisYear"}
                                />
                            </div>

                            {/* Date Inputs */}
                            <div className="flex flex-col sm:flex-row gap-3">
                                <div className="relative">
                                    <input
                                        type="date"
                                        name="startDate"
                                        value={startDate}
                                        onChange={handleFormDate}
                                        min="2023-01-01"
                                        max={new Date().toISOString().split('T')[0]}
                                        className={`w-full sm:w-auto px-4 py-2.5 rounded-xl border-2 cursor-pointer transition-all text-sm font-medium ${selectedBtn === "range"
                                            ? 'border-main-blue bg-main-blue/5 text-main-blue'
                                            : 'border-main-border text-main-subfont bg-main-white hover:border-main-blue/50'
                                            } focus:border-main-blue focus:outline-none focus:ring-4 focus:ring-main-blue/10`}
                                    />
                                </div>
                                <div className="hidden sm:flex items-center justify-center">
                                    <FiChevronRight className="text-main-subfont" />
                                </div>
                                <div className="relative">
                                    <input
                                        type="date"
                                        name="endDate"
                                        value={endDate}
                                        onChange={handleFormDate}
                                        min="2023-01-01"
                                        max={new Date().toISOString().split('T')[0]}
                                        className={`w-full sm:w-auto px-4 py-2.5 rounded-xl border-2 cursor-pointer transition-all text-sm font-medium ${selectedBtn === "range"
                                            ? 'border-main-blue bg-main-blue/5 text-main-blue'
                                            : 'border-main-border text-main-subfont bg-main-white hover:border-main-blue/50'
                                            } focus:border-main-blue focus:outline-none focus:ring-4 focus:ring-main-blue/10`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Event Type Filter */}
                    <div className="px-5 py-4 border-t border-main-border bg-main-th/50">
                        <div className="flex items-start justify-between gap-4 flex-wrap">

                            {/* LEFT : Filters */}
                            <div className="flex items-center gap-3 flex-wrap">
                                <span className="text-sm font-semibold text-main-font">Filter:</span>

                                <button
                                    onClick={() => setSelectedEventFilter('all')}
                                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${selectedEventFilter === 'all'
                                        ? 'bg-main-blue text-main-white shadow-lg shadow-main-blue/25'
                                        : 'bg-main-white text-main-subfont border border-main-border hover:border-main-blue/50 hover:text-main-blue'
                                        }`}
                                >
                                    All Events
                                </button>

                                {eventTypes.map((type) => {
                                    const config = eventConfig[type];
                                    return (
                                        <button
                                            key={type}
                                            onClick={() => setSelectedEventFilter(type)}
                                            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 ${selectedEventFilter === type
                                                ? `${config.bgColor} ${config.textColor} border-2 ${config.borderColor}`
                                                : 'bg-main-white text-main-subfont border border-main-border hover:border-main-blue/50'
                                                }`}
                                        >
                                            <config.icon className="text-base" />
                                            {config.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* RIGHT : Action Buttons */}
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={fetchData}
                                    disabled={loading}
                                    className="px-5 py-2.5 bg-main-white text-main-font rounded-xl font-semibold hover:bg-main-th transition-all flex items-center gap-2 text-sm border border-main-border disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Refresh data"
                                >
                                    <FiRefreshCw className={`text-lg ${loading ? 'animate-spin' : ''}`} />
                                </button>

                                <button
                                    onClick={handleExportCSV}
                                    className="px-5 py-2.5 bg-main-blue text-main-white rounded-xl font-semibold hover:bg-emerald-600 transition-all flex items-center gap-2 text-sm shadow-lg shadow-main-blue/25"
                                >
                                    <FiDownload className="text-lg" />
                                </button>

                                <button
                                    onClick={() => setShowDeleteModal(true)}
                                    className="px-5 py-2.5 bg-main-red text-main-white rounded-xl font-semibold hover:bg-red-600 transition-all flex items-center gap-2 text-sm shadow-lg shadow-main-red/25"
                                >
                                    <FiXCircle className="text-lg" />
                                </button>
                            </div>

                        </div>
                    </div>

                </div>

                {/* Loading Overlay */}
                {loading && <Loader loader={loading} />}

                {/* Delete Modal */}
                {showDeleteModal && <DeleteModal />}

                {/* Total Events Card */}
                <div className="mb-6">
                    <div className="bg-gradient-to-br from-main-blue to-emerald-600 rounded-2xl shadow-xl shadow-main-blue/20 p-6 text-main-white relative overflow-hidden">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
                            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
                        </div>

                        <div className="relative flex items-center justify-between">
                            <div>
                                <p className="text-emerald-100 text-sm font-medium uppercase tracking-widest mb-2">Total Events</p>
                                <p className="text-5xl font-bold tracking-tight">{analytics.total.toLocaleString()}</p>
                                <div className="flex items-center gap-2 mt-3 text-emerald-100">
                                    <FiCalendar className="text-sm" />
                                    <span className="text-sm font-medium">{startDate} → {endDate}</span>
                                </div>
                            </div>
                            <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center">
                                <FiActivity className="text-5xl" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="mb-6">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 bg-main-exBlue/10 rounded-xl flex items-center justify-center">
                            <FiTrendingUp className="text-main-exBlue text-xl" />
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-main-font">Event Summary</h3>
                            <p className="text-xs text-main-subfont">Breakdown by event type</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {eventTypes.map((eventType) => {
                            const config = eventConfig[eventType];
                            const Icon = config.icon;
                            const value = analytics[eventType] || 0;
                            const percentage = ((value / analytics.total) * 100 || 0).toFixed(1);

                            return (
                                <div
                                    key={eventType}
                                    onClick={() => setSelectedEventFilter(eventType)}
                                    className={`bg-main-white rounded-2xl border-2 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group ${selectedEventFilter === eventType
                                        ? `${config.borderColor} shadow-lg`
                                        : 'border-main-border hover:border-main-blue/30'
                                        }`}
                                >
                                    <div className={`h-1 bg-gradient-to-r ${config.gradient}`}></div>
                                    <div className="p-5">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all group-hover:scale-110`} style={{ backgroundColor: config.lightBg }}>
                                                <Icon className={`${config.textColor} text-2xl`} />
                                            </div>
                                            <span className={`text-xs font-bold ${config.textColor} px-2.5 py-1 rounded-lg`} style={{ backgroundColor: config.lightBg }}>
                                                {percentage}%
                                            </span>
                                        </div>
                                        <p className="text-xs font-semibold text-main-subfont uppercase tracking-wide mb-2">
                                            {config.label}
                                        </p>
                                        <p className="text-3xl font-bold text-main-font tracking-tight">
                                            {value.toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ECharts Area Chart Section */}
                <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden mb-6">
                    <div className="p-5 border-b border-main-border">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-main-yellow/10 rounded-xl flex items-center justify-center">
                                    <FiActivity className="text-main-yellow text-xl" />
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold text-main-font">Events Timeline</h2>
                                    <p className="text-xs text-main-subfont mt-0.5">Track event trends over time</p>
                                </div>
                            </div>
                            <span className="text-xs font-semibold text-main-subfont bg-main-bg px-3 py-1.5 rounded-lg border border-main-border">
                                {filteredData.length} records
                            </span>
                        </div>
                    </div>
                    <div className="p-5">
                        {filteredData.length > 0 ? (
                            <div ref={chartRef} style={{ width: '100%', height: '400px' }} />
                        ) : (
                            <div className="flex flex-col items-center justify-center py-20 text-main-subfont">
                                <div className="w-20 h-20 bg-main-bg rounded-full flex items-center justify-center mb-5">
                                    <FiActivity className="text-4xl" />
                                </div>
                                <p className="text-lg font-semibold text-main-font">No events found</p>
                                <p className="text-sm mt-1">Try selecting a different date range or filter</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Table Section */}
                <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                    <div className="p-5 border-b border-main-border">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-main-exBlue/10 rounded-xl flex items-center justify-center">
                                    <FiFilter className="text-main-exBlue text-xl" />
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold text-main-font">Event Records</h2>
                                    <p className="text-xs text-main-subfont mt-0.5">{filteredData.length} records found</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead>
                                <tr className="bg-main-th">
                                    <th className="px-6 py-4 text-left text-xs font-bold text-main-font uppercase tracking-wider">
                                        #
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-main-font uppercase tracking-wider">
                                        Event Type
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-bold text-main-font uppercase tracking-wider">
                                        Count
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-bold text-main-font uppercase tracking-wider">
                                        Created At
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-bold text-main-font uppercase tracking-wider">
                                        Updated At
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-main-border">
                                {filteredData.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-20 text-center">
                                            <div className="flex flex-col items-center">
                                                <div className="w-16 h-16 bg-main-bg rounded-full flex items-center justify-center mb-4">
                                                    <FiBox className="text-3xl text-main-subfont" />
                                                </div>
                                                <p className="text-main-font font-semibold">No records found</p>
                                                <p className="text-sm text-main-subfont mt-1">Try adjusting your filters</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredData.map((item, i) => {
                                        const config = eventConfig[item.a_event] || {};
                                        const Icon = config.icon || FiBox;

                                        return (
                                            <tr
                                                key={item._id}
                                                className="hover:bg-main-thHover transition-colors"
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="text-sm font-bold text-main-font bg-main-bg w-8 h-8 rounded-lg flex items-center justify-center">
                                                        {i + 1}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center`} style={{ backgroundColor: config.lightBg || '#f3f4f6' }}>
                                                            <Icon className={`${config.textColor || 'text-gray-600'} text-xl`} />
                                                        </div>
                                                        <span className={`px-3 py-1.5 text-xs font-bold rounded-lg ${config.textColor || 'text-gray-700'}`} style={{ backgroundColor: config.lightBg || '#f3f4f6' }}>
                                                            {config.label || item.a_event}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    <span className="text-2xl font-bold text-main-font">
                                                        {item.a_count?.toLocaleString() || 0}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    <span className="text-sm font-medium text-main-subfont bg-main-bg px-3 py-1.5 rounded-lg">
                                                        {formatDate(item.createdAt)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    <span className="text-sm font-medium text-main-subfont bg-main-bg px-3 py-1.5 rounded-lg">
                                                        {formatDate(item.updatedAt)}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShowBoxTrack;